import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { InputBase, Link, Box, useTheme } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { useDebounce } from 'packages/core'
import { useTranslation } from 'react-i18next'
import ThresholdSearchInput from '../SearchInput/ThresholdSearchInput'
function Search({
    handleSearch,
    styleData,
    placeholder,
    value,
    showClearIcon = true,
    onEnterPress = null,
    searchInfoMessage = 'Min_Search_Char_Limit',
}) {
    const theme = useTheme()
    const { t } = useTranslation()

    const [searchKey, setSearchKey] = useState(value)

    useEffect(() => {
        if (value !== searchKey) {
            setSearchKey(value)
        }
    }, [value])

    const handleChange = (eventOrValue) => {
        const keyword = eventOrValue?.target?.value ?? eventOrValue
        setSearchKey(keyword)
    }

    const handleClearSearch = () => {
        setSearchKey('')
    }

    const debouncedValue = useDebounce(searchKey)

    useEffect(() => {
        handleSearch(debouncedValue)
    }, [debouncedValue])

    return (
        <Box sx={{ display: 'flex', width: '100%' }}>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                    borderRadius: '5px',
                    margin: 'auto',
                    boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                    border: 'solid 1px #ebebed',
                    backgroundColor: '#fff',
                    height: '40px',
                    transition: 'border 0.15s ease-in-out',

                    '&:hover, &:focus-within': {
                        border: `0.1rem solid ${theme?.palette?.primary?.main}`,
                    },
                }}
            >
                <Box
                    style={{
                        width: 19,
                        height: 19,
                        marginLeft: 17,
                    }}
                >
                    <Icon
                        style={{ height: '19px', width: '19px' }}
                        name="Search"
                        color={styleData.iconColor}
                    />
                </Box>
                <Box position="relative">
                    <ThresholdSearchInput
                        fullWidth
                        value={searchKey || ''}
                        handleSearch={(e) => handleChange(e)}
                        placeholder={placeholder ?? 'Search'}
                        searchInfoMessage={searchInfoMessage}
                        inputProps={{
                            'aria-label': placeholder ?? 'Search',
                        }}
                        sx={{
                            '& > input': {
                                width: '200px',
                                height: '10px',
                                border: 'none',
                                boxShadow: 'none !important',
                            },
                            padding: '1rem 0 0.8rem',
                            flex: 1,
                            color: `${styleData.color} !important`,
                            '& .MuiInputBase-input': {
                                fontSize: '1.6rem',
                                color: `${styleData.color} !important`,

                                '&:hover': {
                                    color: `${styleData.hoverColor} !important`,
                                },

                                '&:focus-within': {
                                    color: `${styleData.color} !important`,
                                },
                            },
                        }}
                        onEnterPress={onEnterPress}
                        sxNotify={{
                            position: 'absolute',
                            top: '-33px',
                            left: '-36px',
                            zIndex: '9999',
                        }}
                    />
                </Box>
                {searchKey && showClearIcon && (
                    <Link
                        component="button"
                        onClick={handleClearSearch}
                        aria-label="Clear"
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                handleClearSearch()
                            }
                        }}
                        style={{
                            width: 13,
                            height: 13,
                            marginRight: 17,
                            paddingTop: '1px',
                            zIndex: 10,
                        }}
                    >
                        <Icon name="Close" color={styleData.iconColor} />
                    </Link>
                )}
            </Box>
        </Box>
    )
}

Search.propTypes = {
    handleSearch: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
    styleData: PropTypes.shape({
        bgColor: PropTypes.string,
        borderColor: PropTypes.string,
        iconColor: PropTypes.string,
        color: PropTypes.string,
        hoverColor: PropTypes.string,
    }),
    placeholder: PropTypes.string,
    showClearIcon: PropTypes.bool,
}

Search.defaultProps = {
    value: '',
    styleData: {
        bgColor: '#f7f8fa',
        borderColor: '#ebebed',
        iconColor: '#b5b6b8',
        color: '#000000',
        hoverColor: '#000000',
    },
    placeholder: 'Search',
    showClearIcon: true,
}

export default Search
